import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Progress } from "@/components/_atoms/progress";
import { FilePreview } from "@/components/_organisms/csv-import-dialog/file-preview";
import { isFileWithPreview } from "@/components/_organisms/csv-import-dialog/is-file-with-preview";
import { getProgressBarColors } from "@/domain/inventory/lib/color-utils";
import { formatBytes } from "@/lib/format-bytes";
import { Cross2Icon } from "@radix-ui/react-icons";

interface FileCardProps {
  file: File;
  onRemove: () => void;
  progress?: number;
  disabled?: boolean;
}

export function FileCard({ file, progress, onRemove, disabled = false }: FileCardProps) {
  const { t } = useTranslation("common");
  const removeFileLabel = t("ariaLabels.fileRemove");

  return (
    <div className="relative flex items-center gap-2.5">
      <div className="flex flex-1 gap-2.5">
        {isFileWithPreview(file) ? <FilePreview file={file} /> : null}
        <div className="flex w-full flex-col gap-2">
          <div className="flex flex-col gap-px">
            <p className="line-clamp-1 text-sm font-medium text-foreground/80">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
          </div>
          {progress ? (
            <Progress
              value={progress}
              style={{ background: getProgressBarColors().background }}
              indicatorColor={getProgressBarColors().indicator}
              aria-label={`${file.name}: ${progress}%`}
            />
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-7"
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeFileLabel}
        >
          <Cross2Icon className="size-4" aria-hidden="true" />
          <span className="sr-only">{removeFileLabel}</span>
        </Button>
      </div>
    </div>
  );
}
